import React, { lazy, Suspense, memo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ROUTES } from './routes';
import { ProtectedRoute } from './ProtectedRoute';
import { TeamProtectedRoute } from './TeamProtectedRoute';
import { LoadingSpinner } from '@/components/ui';
import { HomeRedirect } from './HomeRedirect';
import UpdateProfilePage from '@/pages/auth/UpdateProfileForm';
import { superadminRoutes } from '@/superadmin/routes/SuperadminRouter';

// Core components - load immediately for better UX
import DashboardPage from '@/pages/dashboard/DashboardPage';
import MainLayout from '@/layouts/MainLayout';
import AuthLayout from '@/layouts/AuthLayout';

// Auth pages - lazy load
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const TeamLoginPage = lazy(() => import('@/pages/auth/TeamLoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const AccountActivationPage = lazy(() => import('@/pages/auth/AccountActivationPage'));
const ChangePasswordPage = lazy(() => import('@/pages/auth/ChangePasswordPage'));

// Merchant pages - lazy load heavy components only
const TransactionsPage = lazy(() => import('@/pages/merchant/Transactions'));
const WebhooksPage = lazy(() => import('@/pages/merchant/Webhooks/pages/WebhooksPage'));
const TeamsPage = lazy(() => import('@/pages/merchant/Teams/pages/TeamsPage/TeamsPage'));
const SettlementsPage = lazy(() => import('@/pages/merchant/Settlements'));
const InvoicesPage = lazy(() => import('@/pages/merchant/Invoices/pages/InvoicesPage/InvoicesPage'));
const RoutingRulePage = lazy(() => import('@/pages/merchant/RoutingRule/pages/RoutingRule/RoutingRule'));
const ConnectionsPage = lazy(() => import('@/pages/merchant/Connections/pages/ConnectionsPage/ConnectionsPage'));
const ConnectionSetupPage = lazy(() => import('@/pages/merchant/Connections/pages/ConnectionSetupPage/ConnectionSetupPage'));
const EasebuzzConnectionPage = lazy(() => import('@/pages/merchant/Connections/pages/EasebuzzConnectionPage/EasebuzzConnectionPage'));
const PayUConnectionPage = lazy(() => import('@/pages/merchant/Connections/pages/PayUConnectionPage/PayUConnectionPage'));
const CashfreeConnectionPage = lazy(() => import('@/pages/merchant/Connections/pages/CashfreeConnectionPage/CashfreeConnectionPage'));
const SettingsPage = lazy(() => import('@/pages/merchant/Settings/SettingsPage'));

const TestPage = lazy(() => import('@/pages/merchant/Test/testPage'));
const WebhookDetailPage = lazy(
  () => import('@/pages/merchant/Webhooks/pages/WebhookDetailPage/WebhookDetailPage')
);

const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Optimized wrapper component for lazy-loaded routes
const LazyRoute = memo(({ children }: { children: React.ReactNode }) => (
  <Suspense
    fallback={
      <LoadingSpinner
        title="Loading..."
        subtitle="Please wait"
        size="small"
        color="primary"
      />
    }
  >
    {children}
  </Suspense>
));

// Create router configuration
// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouter([
  {
    path: ROUTES.HOME,
    element: <HomeRedirect />,
  },
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'login',
        element: (
          <LazyRoute>
            <LoginPage />
          </LazyRoute>
        ),
      },
      {
        path: 'team-login/:teamId',
        element: (
          <LazyRoute>
            <TeamLoginPage />
          </LazyRoute>
        ),
      },
      {
        path: 'register',
        element: (
          <LazyRoute>
            <RegisterPage />
          </LazyRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <LazyRoute>
            <ForgotPasswordPage />
          </LazyRoute>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <LazyRoute>
            <ResetPasswordPage />
          </LazyRoute>
        ),
      },
      {
        path: 'account-activation',
        element: (
          <LazyRoute>
            <AccountActivationPage />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/dashboard',
        index: true,
        element: <DashboardPage />,
      },
      {
        path: '/connections',
        index: true,
        element: <DashboardPage />,
      },
      {
        path: '/transactions',
        element: (
          <LazyRoute>
            <TransactionsPage />
          </LazyRoute>
        ),
      },
      {
        path: '/webhooks',
        element: (
          <LazyRoute>
            <WebhooksPage />
          </LazyRoute>
        ),
      },
      {
        path: '/webhooks/:webhookId',
        element: (
          <LazyRoute>
            <WebhookDetailPage />
          </LazyRoute>
        ),
      },
      {
        path: '/merchant',
        children: [
          {
            path: 'transactions',
            element: (
              <LazyRoute>
                <TransactionsPage />
              </LazyRoute>
            ),
          },
          {
            path: 'webhooks',
            element: (
              <LazyRoute>
                <WebhooksPage />
              </LazyRoute>
            ),
          },
          {
            path: 'webhooks/:webhookId',
            element: (
              <LazyRoute>
                <WebhookDetailPage />
              </LazyRoute>
            ),
          },
          {
            path: 'teams',
            element: (
              <LazyRoute>
                <TeamsPage />
              </LazyRoute>
            ),
          },
          {
            path: 'settlements',
            element: (
              <LazyRoute>
                <SettlementsPage />
              </LazyRoute>
            ),
          },
          {
            path: 'invoices',
            element: (
              <LazyRoute>
                <InvoicesPage />
              </LazyRoute>
            ),
          },
          {
            path: 'test',
            element: (
              <LazyRoute>
                <TestPage />
              </LazyRoute>
            ),
          },
          {
            path: 'routing-rule',
            element: (
              <LazyRoute>
                <RoutingRulePage />
              </LazyRoute>
            ),
          },
          {
            path: 'connections',
            element: (
              <LazyRoute>
                <ConnectionsPage />
              </LazyRoute>
            ),
          },
          {
            path: 'connections/setup/Easebuzz',
            element: (
              <LazyRoute>
                <EasebuzzConnectionPage />
              </LazyRoute>
            ),
          },
          {
            path: 'connections/setup/PayU',
            element: (
              <LazyRoute>
                <PayUConnectionPage />
              </LazyRoute>
            ),
          },
          {
            path: 'connections/setup/Cashfree',
            element: (
              <LazyRoute>
                <CashfreeConnectionPage />
              </LazyRoute>
            ),
          },
          {
            path: 'connections/setup/:gatewayName',
            element: (
              <LazyRoute>
                <ConnectionSetupPage />
              </LazyRoute>
            ),
          },
        ],
      },
      {
        path: 'profile',
        element: (
          <LazyRoute>
            <UpdateProfilePage />
          </LazyRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyRoute>
            <SettingsPage/>
          </LazyRoute>
        ),
      },
      {
        path: 'change-password',
        element: (
          <LazyRoute>
            <ChangePasswordPage />
          </LazyRoute>
        ),
      },
    ],
  },
  {
    path: '/teamUser',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'dashboard',
        element: (
          <TeamProtectedRoute>
            <DashboardPage />
          </TeamProtectedRoute>
        ),
      },
      {
        path: 'transactions',
        element: (
          <TeamProtectedRoute
            requiredPermissions={['TRANSACTIONS_VIEW']}
            unauthorizedMessage="You are not authorized to view transactions"
          >
            <LazyRoute>
              <TransactionsPage />
            </LazyRoute>
          </TeamProtectedRoute>
        ),
      },
    ],
  },

  {
    path: ROUTES.LOGIN,
    element: <Navigate to="/auth/login" replace />,
  },
  {
    path: ROUTES.REGISTER,
    element: <Navigate to="/auth/register" replace />,
  },
  {
    path: ROUTES.NOT_FOUND,
    element: (
      <LazyRoute>
        <NotFoundPage />
      </LazyRoute>
    ),
  },
  {
    path: ROUTES.UNAUTHORIZED,
    element: (
      <LazyRoute>
        <div>Unauthorized - Access Denied</div>
      </LazyRoute>
    ),
  },
  // Superadmin routes
  ...superadminRoutes,
  {
    path: '*',
    element: <Navigate to={ROUTES.NOT_FOUND} replace />,
  },
]);

const AppRouter: React.FC = () => {
  return null; // Router is handled by RouterProvider in App.tsx
};

export default AppRouter;